<template>
  <div
    :class="[
      'base-card',
      `base-card--${variant}`,
      {
        'base-card--hoverable': hoverable,
        'base-card--clickable': clickable
      }
    ]"
    @click="handleClick"
  >
    <header v-if="$slots.header || title" class="base-card__header">
      <slot name="header">
        <div class="base-card__header-content">
          <h3 v-if="title" class="base-card__title">{{ title }}</h3>
          <p v-if="subtitle" class="base-card__subtitle">{{ subtitle }}</p>
        </div>
        <div v-if="$slots.actions" class="base-card__actions">
          <slot name="actions" />
        </div>
      </slot>
    </header>

    <div v-if="$slots.default" class="base-card__body">
      <slot />
    </div>

    <footer v-if="$slots.footer" class="base-card__footer">
      <slot name="footer" />
    </footer>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'BaseCard',
  emits: ['click'],
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'bordered', 'elevated', 'flat'].includes(value)
    },
    hoverable: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const handleClick = (event) => {
      if (props.clickable) {
        emit('click', event)
      }
    }

    return {
      handleClick
    }
  }
})
</script>

<style scoped>
.base-card {
  display: flex;
  flex-direction: column;
  background-color: var(--color-white);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: all var(--transition-fast);
  width: 100%;
}

/* Variants */
.base-card--default {
  border: 1px solid var(--color-gray-200);
  box-shadow: var(--shadow-sm);
}

.base-card--bordered {
  border: 1px solid var(--color-gray-300);
  box-shadow: none;
}

.base-card--elevated {
  border: none;
  box-shadow: var(--shadow-lg);
}

.base-card--flat {
  border: none;
  box-shadow: none;
}

/* Interactive States */
.base-card--hoverable:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-xl);
}

.base-card--clickable {
  cursor: pointer;
}

.base-card--clickable:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.base-card--clickable:active {
  transform: translateY(0);
  box-shadow: var(--shadow-sm);
}

/* Header */
.base-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-6);
  border-bottom: 1px solid var(--color-gray-100);
  background-color: var(--color-gray-50);
}

.base-card__header-content {
  flex: 1;
  min-width: 0;
}

.base-card__title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-900);
  margin: 0;
  margin-bottom: var(--space-1);
  line-height: 1.4;
}

.base-card__subtitle {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  margin: 0;
  line-height: 1.4;
}

.base-card__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

/* Body */
.base-card__body {
  flex: 1;
  padding: var(--space-6);
  color: var(--color-gray-700);
  line-height: 1.6;
}

/* Footer */
.base-card__footer {
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--color-gray-100);
  background-color: var(--color-gray-50);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
}

/* Variants without header background */
.base-card--flat .base-card__header,
.base-card--bordered .base-card__header {
  background-color: transparent;
  border-bottom-color: var(--color-gray-200);
}

.base-card--flat .base-card__footer,
.base-card--bordered .base-card__footer {
  background-color: transparent;
  border-top-color: var(--color-gray-200);
}

/* Compact variant for smaller cards */
.base-card--compact .base-card__header {
  padding: var(--space-4);
}

.base-card--compact .base-card__body {
  padding: var(--space-4);
}

.base-card--compact .base-card__footer {
  padding: var(--space-3) var(--space-4);
}

/* Empty state styling */
.base-card__body:empty {
  display: none;
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .base-card__header {
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-3);
    padding: var(--space-4);
  }

  .base-card__actions {
    justify-content: flex-end;
    margin-top: var(--space-2);
  }

  .base-card__body {
    padding: var(--space-4);
  }

  .base-card__footer {
    padding: var(--space-3) var(--space-4);
    flex-direction: column;
    align-items: stretch;
  }

  .base-card__footer > * {
    width: 100%;
  }
}

/* Special styling for dashboard cards */
.base-card--dashboard {
  min-height: 200px;
}

.base-card--dashboard .base-card__header {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
  color: var(--color-white);
  border-bottom: none;
}

.base-card--dashboard .base-card__title {
  color: var(--color-white);
}

.base-card--dashboard .base-card__subtitle {
  color: rgba(255, 255, 255, 0.8);
}

/* Focus styles for accessibility */
.base-card--clickable:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.base-card--clickable:focus:not(:focus-visible) {
  outline: none;
}

/* Loading state */
.base-card--loading {
  position: relative;
  pointer-events: none;
}

.base-card--loading::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 1;
  border-radius: inherit;
}

.base-card--loading::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 2rem;
  height: 2rem;
  margin: -1rem 0 0 -1rem;
  border: 2px solid var(--color-gray-300);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  z-index: 2;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>